import * as React from "react"
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import { ImportCSVMaxFileSize } from "../../config/Constants"
import ConfirmationModal from "../ConfirmationModal"
import ImportJobData from "../../api/Administration/CompanyImport/Model/ImportJobData"
import { LocationImportJobCreateObservable } from "../../api/Administration/CompanyImport/ImportJobCreate"
import ImportJobValidateObservable, { ImportJobValidationResult } from "../../api/Administration/CompanyImport/ImportJobValidate"
import ImportJobStartObservable from "../../api/Administration/CompanyImport/ImportJobStart"
import ImportJobCancelObservable from "../../api/Administration/CompanyImport/ImportJobCancel"
import ActionButton from "../ActionButton"
import { ApiRootUrl } from "../../api/ApiUrl"
import FileSaver from "file-saver"

interface LocationImportProps {
  portalID: number
  companyID: number
  onClose: () => void
}

interface LocationImportState {
  step: "file" | "mapping" | "import"
  fileInputKey: string
  fileName?: string
  fileContent?: string
  importData?: ImportJobData
  validationResult?: ImportJobValidationResult
  isSaving: boolean
  isCancelling: boolean
  showCancelConfirmModal: boolean
  showImportConfirmModal: boolean
}

export default class LocationImport extends React.Component<LocationImportProps, LocationImportState> {
  emptyData: LocationImportState = {
    step: "file",
    fileInputKey: "file_input_key",
    fileName: undefined,
    fileContent: undefined,
    importData: undefined,
    validationResult: undefined,
    isSaving: false,
    isCancelling: false,
    showCancelConfirmModal: false,
    showImportConfirmModal: false
  }
  state: LocationImportState = { ...this.emptyData }

  importJobCreateSubscription?: Subscription
  importJobValidateSubscription?: Subscription
  importJobStartSubscription?: Subscription
  importJobCancelSubscription?: Subscription

  componentDidMount() { }

  componentWillUnmount() {
    this.importJobCreateSubscription && this.importJobCreateSubscription.unsubscribe()
    this.importJobValidateSubscription && this.importJobValidateSubscription.unsubscribe()
    this.importJobStartSubscription && this.importJobStartSubscription.unsubscribe()
    this.importJobCancelSubscription && this.importJobCancelSubscription.unsubscribe()
  }

  handleApiError = (error: Error) => {
    this.setState({ isSaving: false, isCancelling: false })
    toast.error(error)
  }

  handleFileStepNext = () => {
    if (this.state.fileContent) {
      this.setState({ isSaving: true })
      this.importJobCreateSubscription && this.importJobCreateSubscription.unsubscribe()
      this.importJobCreateSubscription = LocationImportJobCreateObservable(this.state.fileContent, this.state.fileName || "", this.props.portalID, this.props.companyID, this.state.importData?.ImportJobID).subscribe(
        data => this.setState({
          step: "mapping",
          importData: data,
          isSaving: false
        }),
        error => this.handleApiError(error)
      )
    }
  }

  handleStartImportValidation = () => {
    if (!this.state.importData?.ImportJobID || !this.state.importData?.Records.length) {
      return
    }
    this.setState({ isSaving: true })
    this.importJobValidateSubscription && this.importJobValidateSubscription.unsubscribe()
    this.importJobValidateSubscription = ImportJobValidateObservable(this.state.importData?.ImportJobID).subscribe(
      validationErrors => this.setState({ step: "import", validationResult: validationErrors, isSaving: false }),
      error => this.handleApiError(error)
    )
  }

  performImport = () => {
    this.setState({ isSaving: true, showImportConfirmModal: false })
    this.importJobStartSubscription && this.importJobStartSubscription.unsubscribe()
    this.importJobStartSubscription = ImportJobStartObservable(this.state.importData?.ImportJobID || 0, 0).subscribe(
      (data) => {
        data.Result ? toast.success("Records imported successfully.") : toast.error("Import completed with errors")
        this.props.onClose()
      },
      error => this.handleApiError(error)
    )
  }

  performCancel = () => {
    this.setState({ isCancelling: true })
    this.importJobCancelSubscription && this.importJobCancelSubscription.unsubscribe()
    this.importJobCancelSubscription = ImportJobCancelObservable(this.state.importData?.ImportJobID || 0).subscribe(
      () => {
        toast.success("Created job is cancelled successfully.")
        this.resetState()
      },
      error => this.handleApiError(error)
    )
  }

  resetState = () => this.setState({ ...this.emptyData, fileInputKey: Math.random().toString() })

  handleHandleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (file && file.size > 0 && file.size < ImportCSVMaxFileSize) {
      const ext = file.name.substring(file.name.lastIndexOf(".") + 1)
      if (ext && ext.toLowerCase() === "csv") {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf("base64") + 7
          const fileContent = result.slice(stringLength, result.length)
          if (fileContent) {
            self.setState({ fileName: file.name, fileContent: fileContent })
          } else {
            self.setState({ fileName: undefined, fileContent: undefined })
            toast.error("CSV file is empty.")
          }
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("File type not allowed, Please upload CSV file.")
      }
    } else {
      this.setState({ fileName: undefined, fileContent: undefined })
    }
  }

  calcColspan = (items: string[], i: number, tableColsCount: number) => {
    if (i === items.length - 1) {
      return Math.max(1, tableColsCount - i)
    }
    return 1
  }

  downloadTemplate = () => {
    const csvData = new Blob(["Location Name,Location Code,Address 1,Address 2,City,State,Zip,Province,Country,Phone,Fax,Web Site URL,Time Zone,Lab Code,Lab Account Number,DER Contact First Name,DER Contact Last Name,DER Contact Phone,DER Contact Email\r\n"], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, "location-import-template.csv")
  }

  render() {
    const { step, fileInputKey, fileContent,
      importData, validationResult,
      isSaving, isCancelling, showCancelConfirmModal, showImportConfirmModal
    } = this.state

    const previewTableColsCount = step === "mapping" && importData
      ? Math.max(...importData.Records.map(item => item.length), importData.HeaderItems.length)
      : 0
    return <Modal size="xl" backdrop="static" show onHide={this.props.onClose} dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>LOCATION IMPORT</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0 LocationImport">
        {step === "file" &&
          <Form>
            <Form.Row><Col><h5>UPLOAD FILE</h5></Col></Form.Row>
            <Form.Row>
              <Col xs={5} md={3} className="mt-2"><h6>Import CSV File</h6></Col>
              <Col xs={7} md={9}>
                <Button variant="underline-link" onClick={() => this.downloadTemplate()}>Download Example Spreadsheet</Button>
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column md={3}>Upload file:</Form.Label>
              <Col md={9} className="form-label col-form-label pl-3">
                <input type="file" key={fileInputKey} accept=".csv" onChange={this.handleHandleFileUpload} />
              </Col>
            </Form.Row>
          </Form>}
        {step === "mapping" &&
          <Form>
            <Row><Col><h5>PREVIEW</h5></Col></Row>
            <Table className="mappings-table" responsive bordered>
              <thead>
                {importData && importData.HeaderItems.length > 0 && <tr>{importData.HeaderItems.map((item, i) => <th key={`hr_${item}_${i}`} colSpan={this.calcColspan(importData.HeaderItems, i, previewTableColsCount)}>{item}</th>)}</tr>}
              </thead>
              <tbody>
                {importData && importData.Records.length > 0 && importData.Records.map((row, i) => <tr key={`d_${i}`}>{row.map((item, j) => <td colSpan={this.calcColspan(row, j, previewTableColsCount)} key={`d_${j}_${i}`}>{item}</td>)}</tr>)}
                {importData && importData.Records.length === 0 && <tr><td colSpan={previewTableColsCount}>NO DATA FOUND</td></tr>}
              </tbody>
            </Table>
          </Form>}
        {step === "import" &&
          <Form>
            <Row><Col><h5>IMPORT FILE VALIDATION</h5></Col></Row>
            <Row><Col>Import file validation: ({(validationResult?.TotalRowsCount || 0) - (validationResult?.ValidRowsCount || 0)} of {validationResult?.TotalRowsCount || 0} have errors)</Col></Row>
            {validationResult && validationResult.Rows.length > 0 &&
              <Table className="table-sm" responsive bordered>
                <thead>
                  <tr>
                    <th>Line #</th>
                    <th>Validation errors and warnings</th>
                    {validationResult.Headers.map((item, i) => <th key={`h_${item}+${i}]`}>{item}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {validationResult.Rows.map((row, i) => <tr key={`d_${i}`}>
                    <td>{row.RowNum}</td>
                    <td>
                      {(row.Errors?.length || 0) > 0 && <div className="error-message">
                        <span className="font-weight-bold">Error{row.Errors!.length > 1 ? `s (${row.Errors!.length}):` : ":"}</span>
                        {row.Errors!.map((item, i) => <div className="pt-1" key={`err_${i}`}>{item}</div>)}
                      </div>}
                      {(row.Warnings?.length || 0) > 0 && <div className="warning-message">
                        <span className="font-weight-bold">Warning{row.Warnings!.length > 1 ? `s (${row.Warnings!.length}):` : ":"}</span>
                        {row.Warnings!.map((item, i) => <div className="pt-1" key={`warn_${i}`}>{item}</div>)}
                      </div>}
                    </td>
                    {row.Items.length === validationResult.Headers.length && row.Items.map((item, j) => <td key={`d_${item}_${i}_${j}`}><span>{item}</span></td>)}
                    {row.Items.length !== validationResult.Headers.length && <td colSpan={validationResult.Headers.length}><span className="font-weight-bold">Original line:</span><div>{row.OriginalString}</div></td>}
                  </tr>)}
                </tbody>
              </Table>}
          </Form>}
        <ConfirmationModal show={showCancelConfirmModal}
          title="CANCEL CONFIRMATION"
          textTitle={`Are you sure you want to cancel importing ${validationResult?.ValidRowsCount || 0} of ${validationResult?.TotalRowsCount || 0} records?`}
          text=""
          onConfirm={this.performCancel}
          onHide={() => this.setState({ showCancelConfirmModal: false })} />
        <ConfirmationModal show={showImportConfirmModal}
          title="IMPORT CONFIRMATION"
          textTitle={`You are about to submit ${validationResult?.ValidRowsCount || 0} of ${validationResult?.TotalRowsCount || 0} records for import?`}
          text=""
          onConfirm={this.performImport}
          onHide={() => this.setState({ showImportConfirmModal: false })} />
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {step === "file" && <Button type="reset" variant="secondary" className="fixed-w5" onClick={this.resetState} disabled={isSaving}>RESET</Button>}
        </div>
        <div>
          {step === "file" && <>
            <Button variant="secondary" className="fixed-w5" onClick={this.props.onClose}>CANCEL</Button>
            <ActionButton variant="primary" className="fixed-w5 ml-1" onClick={this.handleFileStepNext} disabled={!fileContent} spin={isSaving} disableOnSpin>NEXT</ActionButton>
          </>}
          {step === "mapping" && <>
            <Button variant="secondary" className="fixed-w5" onClick={() => this.setState({ step: "file" })} disabled={isSaving}>BACK</Button>
            <ActionButton variant="primary" className="fixed-w5 ml-1" onClick={this.handleStartImportValidation} disabled={!importData || importData.Records.length === 0} spin={isSaving} disableOnSpin>SUBMIT FOR VALIDATION</ActionButton>
          </>}
          {step === "import" && <>
            <Button variant="secondary" className="fixed-w5" onClick={() => this.setState({ step: "mapping", validationResult: undefined })} disabled={isSaving || isCancelling}>BACK</Button>
            <ActionButton variant="secondary" className="fixed-w5 ml-1" onClick={() => this.setState({ showCancelConfirmModal: true })} spin={isCancelling} disableOnSpin>CANCEL</ActionButton>
            <ActionButton variant="primary" className="fixed-w5 ml-1" onClick={() => this.setState({ showImportConfirmModal: true })} disabled={isCancelling || !validationResult?.ValidRowsCount} spin={isSaving} disableOnSpin>IMPORT</ActionButton>
          </>}
        </div>
      </Modal.Footer>
    </Modal >
  }

  static defaultProps = { step: "file" }
}
